<script setup lang="ts">
import InputFloatingLabel from "~/components/inputs/InputFloatingLabel.vue";
import Button from "~/components/inputs/Button.vue";
import SelectedAmount from "~/components/ui/SelectedAmount.vue";
import BackButton from "~/components/inputs/BackButton.vue";
import { useFormContainer } from "~/composables/useFormContainer";
import loanProjectTypes from "~/data/loan_project_types.json";

import { useVuelidate } from "@vuelidate/core";
import contactFormRules from "~/rules/contact";
import Validator from "~/utils/validator";
import LoadingSpinner from "~/components/ui/LoadingSpinner.vue";

import CfModal from "~/components/modals/CfModal.vue";
import RequiredDocuments from "../static/RequiredDocuments.vue";
import Perks from "~/components/static/Perks.vue";
import Select from "~/components/inputs/SelectNew.vue";

const {locale} = await useLocale("forms", "contacts", true);

defineComponent({
  name: "Contacts",
});

const props = defineProps({
  progress: { type: Number, required: true },
  emailDisabled: { type: Boolean, default: true },
  cellphoneDisabled: { type: Boolean, default: false },
  nameDisabled: { type: Boolean, default: false },
  surnameDisabled: { type: Boolean, default: false },
  taxCodeDisabled: { type: Boolean, default: false },
  cellphoneRequiredDigits: { type: Number, required: false },
  calculateCF: { type: Boolean, default: false },
  showLoanProjectTypeComponent: { type: Boolean, default: false },
  showSelectedAmount: { type: Boolean, default: true },
  showRequiredDocuments: { type: Boolean, default: true },
  showPerks: { type: Boolean, default: true },
});
const isAmountRevolving = computed(() => isRevolving());

const { FormContainer } = useFormContainer();

const emit = defineEmits(["init", "submit", "goBack"]);

const loading = useState<boolean>("loading");

const value = useState<number>("credit", () => 10000);

const email = useState<string | null>("email", () => null);
const cellphone = useState<string | null>("cellphone", () => null);
const name = useState<string | null>("name", () => null);
const surname = useState<string | null>("surname", () => null);
const taxCode = useState<string | null>("taxCode", () => null);

const loanProjectType = useState<null | number>("loanProjectType", () => null);


const showCfModal = ref(false);
const openCfModal = () => (showCfModal.value = true);
const closeCfModal = () => (showCfModal.value = false);

const taxCodeContainerRef = useTemplateRef('taxCodeContainerRef');

const onCfModalClosed = async () => {

  await nextTick();

  if (taxCodeContainerRef.value) {
    window.scrollTo({
      top: taxCodeContainerRef.value.offsetTop - window.innerHeight / 2,
      left: 0,
      behavior: 'smooth'
    })
  }
};

const v$ = useVuelidate(contactFormRules, {
  email,
  cellphone,
  name,
  surname,
  taxCode,
  cellphoneRequiredDigits: props.cellphoneRequiredDigits,
  loanProjectType,
  isAmountRevolving: isAmountRevolving.value,
  showLoanProjectTypeComponent: props.showLoanProjectTypeComponent
});

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};

const submit = () => {
  if (validate()) {
    emit("submit");
  }
};



onMounted(() => emit("init"));

</script>
<template>
  <FormContainer :progress="progress">
    <div class=" pt-6 leading-tight">
      <form class="form-container" novalidate @submit.prevent="submit">
        <SelectedAmount v-if="showSelectedAmount" :value="value" :custom-class="'text-2xl'" />
        <div class="flex flex-wrap text-gray-700 p-4 pb-8 sm:px-6 lg:px-12 rounded-b-lg relative z-30">
          <h1 class="pageTitle">
            {{ locale?.title }}
          </h1>
          <p class="pageSubtitle">
            {{ locale?.subtitle }}
          </p>

          <div class="w-full lg:w-1/2 lg:pr-2 mb-2 lg:mb-4 email-input">
            <InputFloatingLabel
              v-model="email"
              :label="locale?.email"
              type="email"
              :disabled="emailDisabled"
              :error="v$.email.$error"
              :error-message="Validator.message(v$.email)"
            />
          </div>

          <div class="w-full lg:w-1/2 mb-2">
            <InputFloatingLabel
              v-model="cellphone"
              :label="locale?.cellphone"
              type="tel"
              :disabled="cellphoneDisabled"
              :error="v$.cellphone.$error"
              :error-message="Validator.message(v$.cellphone)"
              :mask="cellphoneMask"
              test-id="cellphone"
            />
            <span class="text-sm italic">{{ locale?.cellphoneLabel }}</span>
          </div>


          <div class="w-full lg:w-1/4 lg:pr-2 mb-2 lg:mb-4">
            <InputFloatingLabel
              v-model="name"
              :label="locale?.name"
              type="text"
              :mask="alphaMask"
              :disabled="nameDisabled"
              :error="v$.name.$error"
              :error-message="Validator.message(v$.name)"
              test-id="name"
            />
          </div>

          <div class="w-full lg:w-1/4 mb-2 lg:mb-4 lg:pr-2 ">
            <InputFloatingLabel
              v-model="surname"
              :label="locale?.surname"
              type="text"
              :mask="alphaMask"
              :disabled="surnameDisabled"
              :error="v$.surname.$error"
              :error-message="Validator.message(v$.surname)"
              test-id="surname"
            />
          </div>
          
          <div class="w-full lg:w-1/2 mb-4 flex flex-col gap-1" ref="taxCodeContainerRef">
            <InputFloatingLabel
              v-model="taxCode"
              :label="locale?.tax_code"
              type="text"
              :mask="taxCodeMask"
              :disabled="taxCodeDisabled"
              :error="v$.taxCode.$error"
              :error-message="Validator.message(v$.taxCode)"
              test-id="taxCode"
            />
            <span v-if="calculateCF" class="text-sm">
              <span class="">Non ricordi il Codice Fiscale? </span>
              <button
                type="button"
                @click="openCfModal"
                class="underline hover:cursor-pointer"
              >
                Calcola
              </button>
            </span>
          </div>
          <template v-if="showLoanProjectTypeComponent && !isAmountRevolving">
            <h1 class="pageTitle my-4">
              {{ locale?.loan_project_type_title }}
            </h1>
            <div class="w-full lg:w-1/2 lg:pr-2 mb-2">
              <Select
              v-model="loanProjectType"
              :values="loanProjectTypes"
              :label="locale?.loan_project_type_label"
              floating
              disable-zero-value
              :error="v$.loanProjectType.$error"
              :error-message="Validator.message(v$.loanProjectType)"
              test-id="loanProjectType"
              @change="v$.$reset()"
              />
            </div>
          </template>
        </div>
        <div class="form-footer text-center">
          <Button class="!px-16" type="submit" data-testid="contactsSubmit">
            <LoadingSpinner v-if="loading"></LoadingSpinner>
            <span v-else>{{ locale?.continue }}</span>
          </Button>
          <BackButton :event="`goBack`" @go-back="$emit('goBack')" />
        </div>
      </form>
    </div>
    <CfModal :show="showCfModal" @close="closeCfModal" @closed="onCfModalClosed" />
  </FormContainer>
  <RequiredDocuments
    v-if="showRequiredDocuments"
    :required_documents="locale?.required_documents"
    :valid_identity_document="locale?.valid_identity_document"
    :valid_identity_document_subtitle="locale?.valid_identity_document_subtitle"
    :iban_current_account="locale?.iban_current_account"
    :iban_current_account_subtitle="locale?.iban_current_account_subtitle"
    custom-class="-mt-6 pb-4"
  />

  <Perks v-if="showPerks" />
  
</template>

<style scoped></style>
